import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const DataTableTab = ({ data, tabName, sheetName, editable = false, onDataUpdate, settings }) => {
  const [editMode, setEditMode] = useState({});
  const [editValues, setEditValues] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-gray-500">No data available for this tab.</p>
        <p className="text-xs text-gray-400 mt-2">Sheet: "{sheetName || tabName}"</p>
      </div>
    );
  }

  // Helper function to check if a cell is protected (read-only)
  const isCellProtected = (rowIdx, cellIdx) => {
    // Setup tab: Column A (index 0) for rows 1-11
    if (tabName === 'Setup' && cellIdx === 0 && rowIdx >= 1 && rowIdx <= 11) {
      return true;
    }
    
    // Benchmarks tab: Column A (index 0) for rows 0-8
    if (tabName === 'Benchmarks' && cellIdx === 0 && rowIdx >= 0 && rowIdx <= 8) {
      return true;
    }
    
    // Benchmarks tab: Row 2 for columns B-D (index 1-3)
    if (tabName === 'Benchmarks' && rowIdx === 2 && cellIdx >= 1 && cellIdx <= 3) {
      return true;
    }
    
    // Assumptions tab: Column A (index 0) for rows 0-24 (A1:A25)
    if (tabName === 'Assumptions' && cellIdx === 0 && rowIdx >= 0 && rowIdx <= 24) {
      return true;
    }
    
    // Assumptions tab: Specific protected cells
    if (tabName === 'Assumptions') {
      const protectedCells = [
        [2, 1],   // B3
        [7, 1],   // B8
        [8, 1],   // B9
        [14, 1],  // B15
        [15, 1],  // B16
        [7, 2],   // C8
        [17, 2]   // C18
      ];
      
      for (const [row, col] of protectedCells) {
        if (rowIdx === row && cellIdx === col) {
          return true;
        }
      }
    }
    
    // Scenarios tab: Column A (index 0) for rows 0-9 (A1:A10)
    if (tabName === 'Scenarios' && cellIdx === 0 && rowIdx >= 0 && rowIdx <= 9) {
      return true;
    }
    
    // Scenarios tab: Row 2 for columns B-G (B3:G3 - scenario parameter headers)
    if (tabName === 'Scenarios' && rowIdx === 2 && cellIdx >= 1 && cellIdx <= 6) {
      return true;
    }
    
    // Scenarios tab: B10 (rowIdx=9, cellIdx=1)
    if (tabName === 'Scenarios' && rowIdx === 9 && cellIdx === 1) {
      return true;
    }
    
    return false;
  };

  // Validation function for Setup tab cells
  const validateCellInput = (rowIdx, cellIdx, value) => {
    // Setup tab specific validations for B3-B6 (rowIdx 2-5, cellIdx 1)
    if (tabName === 'Setup' && cellIdx === 1) {
      // B3 (rowIdx=2) - Accubee Accounting - String
      if (rowIdx === 2) {
        if (typeof value !== 'string' || value.trim() === '') {
          return { valid: false, message: 'B3 should be a string (non-empty text)' };
        }
        return { valid: true };
      }
      
      // B4 (rowIdx=3) - Currency Symbol - String
      if (rowIdx === 3) {
        if (typeof value !== 'string' || value.trim() === '') {
          return { valid: false, message: 'B4 should be a string (e.g., $, €, ₹)' };
        }
        return { valid: true };
      }
      
      // B5 (rowIdx=4) - Number - Numeric
      if (rowIdx === 4) {
        const numValue = Number(value);
        if (isNaN(numValue) || value.trim() === '') {
          return { valid: false, message: 'B5 should be numeric (e.g., 5, 10, 3.5)' };
        }
        return { valid: true };
      }
      
      // B6 (rowIdx=5) - Year - Numeric
      if (rowIdx === 5) {
        const numValue = Number(value);
        if (isNaN(numValue) || value.trim() === '' || !Number.isInteger(numValue)) {
          return { valid: false, message: 'B6 should be numeric (integer year, e.g., 2025)' };
        }
        if (numValue < 1900 || numValue > 2100) {
          return { valid: false, message: 'B6 year must be between 1900 and 2100' };
        }
        return { valid: true };
      }
    }
    
    // Benchmarks tab: B4:D9 validation (rowIdx 3-8, cellIdx 1-3)
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      const numValue = Number(value);
      if (isNaN(numValue) || value.trim() === '') {
        const colName = String.fromCharCode(66 + cellIdx); // B, C, or D
        const rowNum = rowIdx + 1; // Convert to 1-based
        return { valid: false, message: `${colName}${rowNum} should be numeric. Invalid input: expected a number.` };
      }
      return { valid: true };
    }
    
      // Assumptions tab: Numeric-only cells validation
    if (tabName === 'Assumptions' && cellIdx === 1) {
      // B5, B6, B10, B11, B12, B13, B18, B19, B20, B21 (rowIdx 4, 5, 9, 10, 11, 12, 17, 18, 19, 20)
      const numericOnlyCells = [4, 5, 9, 10, 11, 12, 17, 18, 19, 20];
      if (numericOnlyCells.includes(rowIdx)) {
        const numValue = Number(value);
        if (isNaN(numValue) || value.trim() === '') {
          const rowNum = rowIdx + 1;
          return { valid: false, message: `B${rowNum} should be numeric. Invalid input: expected a number.` };
        }
        return { valid: true };
      }
      
      // B7, B17, B22 (rowIdx 6, 16, 21) - Percentage cells
      const percentageCells = [6, 16, 21];
      if (percentageCells.includes(rowIdx)) {
        const numValue = Number(value);
        if (isNaN(numValue) || value.trim() === '') {
          const rowNum = rowIdx + 1;
          return { valid: false, message: `B${rowNum} should be numeric. Invalid input: expected a number.` };
        }
        return { valid: true };
      }
    }
    
    // Scenarios tab: B4:G6 validation (rowIdx 3-5, cellIdx 1-6)
    if (tabName === 'Scenarios' && cellIdx >= 1 && cellIdx <= 6 && rowIdx >= 3 && rowIdx <= 5) {
      const numValue = Number(value);
      if (isNaN(numValue) || value.trim() === '') {
        const colName = String.fromCharCode(66 + cellIdx); // B, C, D, E, F, or G
        const rowNum = rowIdx + 1; // Convert to 1-based
        return { valid: false, message: `${colName}${rowNum}: Only numeric values are allowed. Invalid input: expected a number.` };
      }
      return { valid: true };
    }
    
    return { valid: true };
  };

    // Format value for display (strip % symbol)
  const formatForDisplay = (rowIdx, cellIdx, value) => {
    // Benchmarks tab B4:D9 - strip % symbol
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      if (typeof value === 'string' && value.includes('%')) {
        return value.replace('%', '').trim();
      }
    }
    
    // Assumptions tab B7, B17, B22 (rowIdx 6, 16, 21) - strip % symbol
    if (tabName === 'Assumptions' && cellIdx === 1) {
      const percentageCells = [6, 16, 21];
      if (percentageCells.includes(rowIdx)) {
        if (typeof value === 'string' && value.includes('%')) {
          return value.replace('%', '').trim();
        }
      }
    }
    
    return value;
  };


  // Format value for saving (append % symbol)
  const formatForSaving = (rowIdx, cellIdx, value) => {
    // Benchmarks tab B4:D9 - append % symbol
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      // Remove any existing % and whitespace, then append %
      const cleanValue = String(value).replace('%', '').trim();
      return `${cleanValue}%`;
    }
    
    // Assumptions tab B7, B17, B22 (rowIdx 6, 16, 21) - append % symbol
    if (tabName === 'Assumptions' && cellIdx === 1) {
      const percentageCells = [6, 16, 21];
      if (percentageCells.includes(rowIdx)) {
        const cleanValue = String(value).replace('%', '').trim();
        return `${cleanValue}%`;
      }
    }
    
    return value;
  };

  const handleCellDoubleClick = (rowIdx, cellIdx, currentValue) => {
    if (!editable) return;

    // Prevent editing of title column (column A, index 0) in Setup tab for rows 1-11
    if (tabName === 'Setup' && cellIdx === 0 && rowIdx >= 1 && rowIdx <= 11) {
      toast.info('System field - Cannot be edited');
      return;
    }

    // Prevent editing in Benchmarks tab
    if (tabName === 'Benchmarks') {
      if (cellIdx === 0 && rowIdx >= 0 && rowIdx <= 8) {
        toast.info('System field - Cannot be edited');
        return;
      }
      if (rowIdx === 2 && cellIdx >= 1 && cellIdx <= 3) {
        toast.info('Benchmark header - Cannot be edited');
        return;
      }
    }

    // Prevent editing in Assumptions tab
    if (tabName === 'Assumptions') {
      if (cellIdx === 0 && rowIdx >= 0 && rowIdx <= 24) {
        toast.info('System field - Cannot be edited');
        return;
      }
      
      const protectedCells = [
        [2, 1],   // B3
        [7, 1],   // B8
        [8, 1],   // B9
        [14, 1],  // B15
        [15, 1],  // B16
        [7, 2],   // C8
        [17, 2]   // C18
      ];
      
      for (const [row, col] of protectedCells) {
        if (rowIdx === row && cellIdx === col) {
          toast.info('System-calculated field - Cannot be edited');
          return;
        }
      }
    }

    // Prevent editing in Scenarios tab
    if (tabName === 'Scenarios') {
      if (cellIdx === 0 && rowIdx >= 0 && rowIdx <= 9) {
        toast.info('System field - Cannot be edited');
        return;
      }
      if (rowIdx === 2 && cellIdx >= 1 && cellIdx <= 6) {
        toast.info('Scenario parameter header - Cannot be edited');
        return;
      }
      if (rowIdx === 9 && cellIdx === 1) {
        toast.info('System-calculated field - Cannot be edited');
        return;
      }
    }
    
    const key = `${rowIdx}-${cellIdx}`;
    setEditMode({ ...editMode, [key]: true });
    // Strip % for display in input field
    const displayValue = formatForDisplay(rowIdx, cellIdx, currentValue);
    setEditValues({ ...editValues, [key]: displayValue });
  };

  const handleCellChange = (rowIdx, cellIdx, value) => {
    const key = `${rowIdx}-${cellIdx}`;
    
    // Setup tab validation
    if (tabName === 'Setup' && cellIdx === 1 && (rowIdx === 4 || rowIdx === 5)) {
      if (value !== '' && isNaN(Number(value))) {
        const cellName = rowIdx === 4 ? 'B5' : 'B6';
        toast.warning(`${cellName} should be numeric`);
      }
    }
    
    // Benchmarks tab validation (B4:D9)
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      if (value !== '' && isNaN(Number(value))) {
        const colName = String.fromCharCode(66 + cellIdx); // B, C, or D
        const rowNum = rowIdx + 1;
        toast.warning(`${colName}${rowNum} should be numeric`);
      }
    }
    
    // Assumptions tab validation
    if (tabName === 'Assumptions' && cellIdx === 1) {
      const numericCells = [4, 5, 6, 9, 10, 11, 12, 16, 17, 18, 19, 20, 21];
      if (numericCells.includes(rowIdx)) {
        if (value !== '' && isNaN(Number(value))) {
          const rowNum = rowIdx + 1;
          toast.warning(`B${rowNum} should be numeric`);
        }
      }
    }
    
    // Scenarios tab validation (B4:G6)
    if (tabName === 'Scenarios' && cellIdx >= 1 && cellIdx <= 6 && rowIdx >= 3 && rowIdx <= 5) {
      if (value !== '' && isNaN(Number(value))) {
        const colName = String.fromCharCode(66 + cellIdx); // B, C, D, E, F, or G
        const rowNum = rowIdx + 1;
        toast.warning(`${colName}${rowNum}: Only numeric values are allowed`);
      }
    }
    
    setEditValues({ ...editValues, [key]: value });
  };

  // Real-time validation as user types
  const validateOnChange = (rowIdx, cellIdx, value) => {
    if (tabName === 'Setup' && cellIdx === 1) {
      // B5 and B6 should only accept numeric input
      if ((rowIdx === 4 || rowIdx === 5) && value !== '') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          const cellName = rowIdx === 4 ? 'B5' : 'B6';
          toast.error(`${cellName} should be numeric`);
          return false;
        }
      }
    }
    return true;
  };

  const handleCellBlur = async (rowIdx, cellIdx) => {
    const key = `${rowIdx}-${cellIdx}`;
    const newValue = editValues[key];
    
    // Validate input before saving
    const validation = validateCellInput(rowIdx, cellIdx, newValue);
    if (!validation.valid) {
      toast.error(validation.message);
      setEditMode({ ...editMode, [key]: false });
      // Reset to original value
      const originalValue = data[rowIdx] ? data[rowIdx][cellIdx] : '';
      setEditValues({ ...editValues, [key]: originalValue });
      return;
    }
    
    setEditMode({ ...editMode, [key]: false });

    try {
       // Format value before saving (add % for Benchmarks)
      const formattedValue = formatForSaving(rowIdx, cellIdx, newValue);
      
      await axios.put(`${API}/sheets/update-cell`, {
        sheet_name: sheetName || tabName,
        row: rowIdx,
        col: cellIdx,
        value: formattedValue
      });
      toast.success('Editing in Progress..');
      
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      console.error('Error updating cell:', error);
      toast.error('Failed to update cell');
    }
  };

  const getCellValue = (rowIdx, cellIdx, originalValue) => {
    const key = `${rowIdx}-${cellIdx}`;
    const value = editValues[key] !== undefined ? editValues[key] : originalValue;
    // Format for display (strip % if in Benchmarks)
    return formatForDisplay(rowIdx, cellIdx, value);
  };

  const isEditMode = (rowIdx, cellIdx) => {
    const key = `${rowIdx}-${cellIdx}`;
    return editMode[key] || false;
  };

  // Get input type for validated cells
  const getInputType = (rowIdx, cellIdx) => {
    if (tabName === 'Setup' && cellIdx === 1) {
      // B5 and B6 should be number inputs
      if (rowIdx === 4 || rowIdx === 5) {
        return 'number';
      }
      // B3 and B4 should be text inputs
      if (rowIdx === 2 || rowIdx === 3) {
        return 'text';
      }
    }
    
    // Benchmarks tab B4:D9 should be number inputs
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      return 'number';
    }
    
    // Assumptions tab numeric cells
    if (tabName === 'Assumptions' && cellIdx === 1) {
      // B5, B6, B7, B10, B11, B12, B13, B17, B18, B19, B20, B21, B22
      const numericCells = [4, 5, 6, 9, 10, 11, 12, 16, 17, 18, 19, 20, 21];
      if (numericCells.includes(rowIdx)) {
        return 'number';
      }
    }
    
    // Scenarios tab B4:G6 should be number inputs
    if (tabName === 'Scenarios' && cellIdx >= 1 && cellIdx <= 6 && rowIdx >= 3 && rowIdx <= 5) {
      return 'number';
    }
    
    return 'text';
  };

  // Get placeholder text for validated cells
  const getPlaceholder = (rowIdx, cellIdx) => {
    if (tabName === 'Setup' && cellIdx === 1) {
      if (rowIdx === 2) return 'Enter company name (string)';
      if (rowIdx === 3) return 'Enter currency symbol (string)';
      if (rowIdx === 4) return 'Enter number (numeric)';
      if (rowIdx === 5) return 'Enter year (numeric)';
    }
    
    // Benchmarks tab B4:D9
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      return 'Enter percentage value (numeric, % added automatically)';
    }
    
    // Assumptions tab
    if (tabName === 'Assumptions' && cellIdx === 1) {
      // Percentage cells: B7, B17, B22
      const percentageCells = [6, 16, 21];
      if (percentageCells.includes(rowIdx)) {
        return 'Enter percentage value (numeric, % added automatically)';
      }
      
      // Regular numeric cells
      const numericCells = [4, 5, 9, 10, 11, 12, 17, 18, 19, 20];
      if (numericCells.includes(rowIdx)) {
        return 'Enter numeric value';
      }
    }
    
    // Scenarios tab B4:G6
    if (tabName === 'Scenarios' && cellIdx >= 1 && cellIdx <= 6 && rowIdx >= 3 && rowIdx <= 5) {
      return 'Enter numeric value';
    }
    
    return '';
  };

  // Get step attribute for number inputs
  const getStep = (rowIdx, cellIdx) => {
    if (tabName === 'Setup' && cellIdx === 1) {
      if (rowIdx === 4) return 'any'; // B5 allows decimals
      if (rowIdx === 5) return '1';   // B6 only integers
    }
    
   // Benchmarks tab B4:D9 allows decimals
    if (tabName === 'Benchmarks' && cellIdx >= 1 && cellIdx <= 3 && rowIdx >= 3 && rowIdx <= 8) {
      return 'any';
    }
    
    // Assumptions tab numeric cells allow decimals
    if (tabName === 'Assumptions' && cellIdx === 1) {
      const numericCells = [4, 5, 6, 9, 10, 11, 12, 16, 17, 18, 19, 20, 21];
      if (numericCells.includes(rowIdx)) {
        return 'any';
      }
    }
    
    // Scenarios tab B4:G6 allows decimals
    if (tabName === 'Scenarios' && cellIdx >= 1 && cellIdx <= 6 && rowIdx >= 3 && rowIdx <= 5) {
      return 'any';
    }
    
    return undefined;
  };

  // Special handling for dropdown fields in Setup sheet and Scenarios sheet
  const isDropdownField = (rowIdx, cellIdx, cellValue) => {
    const headerValue = data[rowIdx] ? data[rowIdx][0] : '';
    
    // Setup sheet dropdowns
    if (tabName === 'Setup' && cellIdx === 1) {
      return headerValue.includes('Consolidation') || 
             headerValue.includes('Business Model') || 
             headerValue.includes('Industry');
    }
    
    // Scenarios sheet - Active Scenario dropdown
    if (tabName === 'Scenarios' && cellIdx === 1) {
      return headerValue.includes('Active Scenario');
    }
    
    return false;
  };

  const getDropdownOptions = (headerValue) => {
    if (headerValue.includes('Consolidation')) {
      return ['Yes', 'No'];
    } else if (headerValue.includes('Business Model')) {
      return ['Manufacturing', 'Services', 'SaaS', 'Retail', 'Logistics'];
    } else if (headerValue.includes('Industry')) {
      return ['FMCG', 'IT Services', 'Auto', 'Logistics', 'Education', 'Other'];
    } else if (headerValue.includes('Active Scenario')) {
      return ['Base', 'Best', 'Worst'];
    }
    return [];
  };

  const handleDropdownChange = async (rowIdx, cellIdx, value) => {
    try {
      await axios.put(`${API}/sheets/update-cell`, {
        sheet_name: sheetName || tabName,
        row: rowIdx,
        col: cellIdx,
        value: value
      });
      toast.success('Updated successfully!');
      
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Failed to update');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 mr-4 mt-4" data-testid={`data-table-${tabName}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 capitalize flex items-center gap-2">
          <span className="w-1 h-8 bg-indigo-500 rounded-full"></span>
          {tabName.replace('_', ' & ')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Data from Google Sheets - "{sheetName || tabName}" sheet
          {editable && <span className="ml-2 text-indigo-600 font-medium">(Double-click to edit)</span>}
        </p>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b-2 border-indigo-200">
              {data[0] && data[0].map((header, idx) => (
                <th
                  key={idx}
                  className="px-6 py-4 text-left text-sm font-bold text-gray-900 border-r border-gray-200 last:border-r-0"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(1).map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150"
              >
                {row.map((cell, cellIdx) => {
                  const actualRowIdx = rowIdx + 1;
                  const headerValue = data[actualRowIdx] ? data[actualRowIdx][0] : '';
                  
                  return (
                    <td
                      key={cellIdx}
                      className={`px-6 py-4 text-sm text-gray-700 border-r border-gray-200 last:border-r-0 font-mono ${
                        editable && !isCellProtected(actualRowIdx, cellIdx)
    ? 'cursor-pointer hover:bg-indigo-50' 
    : isCellProtected(actualRowIdx, cellIdx)
    ? 'bg-gray-100 text-gray-400'  
    : ''
                      }`}
                      onDoubleClick={() => handleCellDoubleClick(actualRowIdx, cellIdx, cell)}
                    >
                      {editable && isDropdownField(actualRowIdx, cellIdx, cell) ? (
                        <Select
                          value={cell || ''}
                          onValueChange={(value) => handleDropdownChange(actualRowIdx, cellIdx, value)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getDropdownOptions(headerValue).map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : isEditMode(actualRowIdx, cellIdx) ? (
                        <input
                          type={getInputType(actualRowIdx, cellIdx)}
                          value={getCellValue(actualRowIdx, cellIdx, cell)}
                          onChange={(e) => handleCellChange(actualRowIdx, cellIdx, e.target.value)}
                          onBlur={() => handleCellBlur(actualRowIdx, cellIdx)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleCellBlur(actualRowIdx, cellIdx);
                            } else if (e.key === 'Escape') {
                              const key = `${actualRowIdx}-${cellIdx}`;
                              setEditMode({ ...editMode, [key]: false });
                            }
                          }}
                          placeholder={getPlaceholder(actualRowIdx, cellIdx)}
                          step={getStep(actualRowIdx, cellIdx)}
                          className="w-full px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                      ) : (
                        cell || '-'
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Live data from Google Sheets</span>
        </div>
        <span>{data.length - 1} rows × {data[0]?.length || 0} columns</span>
      </div>
    </div>
  );
};

export default DataTableTab;